// API utilities for the Prediction Explorer

const API_BASE = '/api/v1'
const API_KEY_STORAGE = 'cenva_api_key'

export function getStoredApiKey(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const key = window.localStorage.getItem(API_KEY_STORAGE)
    return key && key.trim() ? key.trim() : null
  } catch {
    return null
  }
}

export function setStoredApiKey(key: string | null): void {
  if (typeof window === 'undefined') return
  try {
    if (key && key.trim()) {
      window.localStorage.setItem(API_KEY_STORAGE, key.trim())
    } else {
      window.localStorage.removeItem(API_KEY_STORAGE)
    }
  } catch {
    // Ignore storage errors (private mode, disabled storage, etc.)
  }
}

function formatApiError(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail || fallback
  if (Array.isArray(detail)) {
    const asText = detail
      .map((item) => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object' && 'msg' in item && typeof (item as any).msg === 'string') {
          return (item as any).msg as string
        }
        try {
          return JSON.stringify(item)
        } catch {
          return String(item)
        }
      })
      .filter(Boolean)
      .join('\n')
    return asText || fallback
  }
  if (detail && typeof detail === 'object') {
    const message = (detail as any).message
    if (typeof message === 'string' && message.trim()) return message
    try {
      return JSON.stringify(detail)
    } catch {
      return fallback
    }
  }
  return fallback
}

interface FetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean | null | undefined>
}

async function apiFetch<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options
  
  let url = `${API_BASE}${endpoint}`
  
  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        searchParams.append(key, String(value))
      }
    })
    const queryString = searchParams.toString()
    if (queryString) {
      url += `?${queryString}`
    }
  }
  
  const apiKey = getStoredApiKey()
  const response = await fetch(url, {
    credentials: 'include',
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      ...(apiKey ? { 'X-API-Key': apiKey } : {}),
      ...fetchOptions.headers,
    },
  })
  
  if (!response.ok) {
    const errorJson = await response.json().catch(() => null)
    const detail = (errorJson as any)?.detail ?? (errorJson as any)?.message ?? response.statusText
    const message = formatApiError(detail, `API error: ${response.status}`)
    throw new Error(message)
  }
  
  return response.json()
}

// Health API
export const healthApi = {
  get: () =>
    apiFetch<{ status: string; db: string; server_time: string }>('/health'),
}

// Questions API
export const questionsApi = {
  list: (params: {
    page?: number
    page_size?: number
    category?: string | null
    status?: string | null
    has_run?: boolean | null
    has_position?: boolean | null
    search?: string
    sort?: string
  }) => apiFetch<import('../types').QuestionListResponse>('/questions', { params }),
  
  get: (questionId: number) =>
    apiFetch<import('../types').QuestionDetail>(`/questions/${questionId}`),
  
  categories: () => apiFetch<{ categories: string[] }>('/questions/categories'),
}

// Runs API
export interface AgentSummary {
  name: string
  status: 'complete' | 'running' | 'pending'
  prob: number | null
  confidence: number | null
  summary: string | null
  evidence_count: number
  completed_at: string | null
}

export interface OutcomePrediction {
  qid: number
  name: string
  prob: number
  edge: number | null
  market_prob?: number
}

export interface RecentRunItem {
  run_id: number
  question_id: number
  question_title: string
  analyzed_outcome_title: string | null  // Specific child market/outcome being predicted
  started_at: string | null
  ended_at: string | null
  status: 'running' | 'completed' | 'pending'
  orchestrator_version: string | null
  post_prob: number | null
  confidence: number | null
  market_price: number | null
  edge: number | null
  agents_completed: number
  agents_total: number
  agents: AgentSummary[]
  decision: 'buy' | 'sell' | 'hold' | null
  majority_vote: boolean | null
  outcome_probabilities: OutcomePrediction[] | null  // Multi-outcome predictions
}

export const runsApi = {
  get: (runId: number) => apiFetch<import('../types').Run>(`/runs/${runId}`),

  getAgent: (runId: number, agentName: string) =>
    apiFetch<import('../types').AgentRun & { evidence: unknown[]; claims: unknown[] }>(
      `/runs/${runId}/agents/${agentName}`
    ),

  start: (questionId: number) =>
    apiFetch<{ run_id: number; question_id: number; status: string; message: string }>(
      `/runs/question/${questionId}`,
      { method: 'POST' }
    ),

  history: (questionId: number, limit?: number) =>
    apiFetch<{ question_id: number; runs: import('../types').RunHistoryItem[] }>(
      `/runs/question/${questionId}/history`,
      { params: { limit: limit || 20 } }
    ),

  recent: (limit?: number, includeRunning?: boolean) =>
    apiFetch<{ runs: RecentRunItem[]; count: number }>(
      `/runs/recent`,
      { params: { limit: limit || 50, include_running: includeRunning !== false } }
    ),
}

// Summaries API
export const summariesApi = {
  get: (runId: number) =>
    apiFetch<import('../types').RunSummary>(`/summaries/run/${runId}`),
  
  generate: (runId: number, regenerate?: boolean) =>
    apiFetch<import('../types').RunSummary>(`/summaries/run/${runId}`, {
      method: 'POST',
      body: JSON.stringify({ regenerate: regenerate || false }),
    }),
  
  listForQuestion: (questionId: number, limit?: number) =>
    apiFetch<{ question_id: number; summaries: import('../types').RunSummary[]; count: number }>(
      `/summaries/question/${questionId}`,
      { params: { limit: limit || 20 } }
    ),
  
  delete: (runId: number) =>
    apiFetch<{ message: string }>(`/summaries/run/${runId}`, { method: 'DELETE' }),
}

// Admin API types
export interface DataFreshness {
  has_stale_market_data: boolean
  stale_position_count: number
  oldest_market_data_mins: number | null
  last_run_mins_ago: number | null
  unsettled_resolved_count: number
}

export interface DashboardStats {
  total_questions: number
  open_questions: number
  total_runs: number
  runs_24h: number
  runs_7d: number
  avg_brier: number | null
  avg_brier_7d: number | null
  total_positions: number
  total_exposure: number
  total_pnl: number
  unrealized_pnl: number
  pnl_7d: number
  data_freshness: DataFreshness | null
}

export interface DashboardCard {
  question_id: number
  title: string
  category: string | null
  question_role: string | null
  analyzed_outcome_name: string | null
  our_prob: number | null
  market_prob: number | null
  edge: number | null
  last_run_at: string | null
  close_ts: string | null
  spread_bps: number | null
  liquidity: number | null
}

export interface NeedsAttentionItem {
  question_id: number
  title: string
  category: string | null
  reason: string
  urgency: 'critical' | 'warning' | 'info'
  edge: number | null
  close_ts: string | null
  hours_to_close: number | null
}

export interface CategoryExposure {
  category: string
  exposure: number
  pct_of_total: number
  position_count: number
  avg_edge: number | null
}

export interface RiskSummary {
  largest_position_title: string | null
  largest_position_exposure: number
  concentration_warning: string | null
  positions_closing_soon: number
  positions_with_negative_edge: number
  category_breakdown: CategoryExposure[]
}

export interface DashboardResponse {
  stats: DashboardStats
  recent_predictions: DashboardCard[]
  needs_attention: NeedsAttentionItem[]
  risk_summary: RiskSummary
}

// Refresh Market Data types
export interface RefreshMarketDataResponse {
  success: boolean
  events_seen: number
  markets_seen: number
  qualified_saved: number
  message: string
}

// Admin API
export const adminApi = {
  dashboard: () => apiFetch<DashboardResponse>('/admin/dashboard'),

  refreshMarketData: () =>
    apiFetch<RefreshMarketDataResponse>('/admin/refresh-market-data', { method: 'POST' }),

  skills: () =>
    apiFetch<{
      skills: {
        skill_name: string
        summary: string
        topic: string | null
        status: string | null
        apis: { name: string; base_url?: string }[]
        example_inputs: Record<string, unknown>
        last_verified_at: string | null
        origin_question_id: number | null
      }[]
      total: number
    }>('/admin/skills'),

  testSkill: (skill_name: string, inputs_json: Record<string, unknown>) =>
    apiFetch<{
      success: boolean
      result?: Record<string, unknown>
      error?: string
      execution_time_ms?: number
    }>('/admin/skills/test', {
      method: 'POST',
      body: JSON.stringify({ skill_name, inputs_json }),
    }),

  memory: (category?: string, offset?: number) =>
    apiFetch<{
      stats: {
        working_total: number
        total_entries: number
        avg_brier: number | null
        working_added_24h: number
      }
      working_entries: {
        id: number
        type: string
        content: string
        weight: number
        reliability: number
        age_hours: number
        access_count: number
        source_domain: string | null
        topics: string[]
      }[]
      episodes: {
        id: number
        question_id: number
        question_text: string
        category: string | null
        prediction_prob: number | null
        actual_outcome: number | null
        brier_score: number | null
        key_insights: string[]
        created_at: string | null
      }[]
      successes: {
        id: number
        question_id: number
        question_text: string
        category: string | null
        prediction_prob: number | null
        actual_outcome: number | null
        brier_score: number | null
        key_insights: string[]
        created_at: string | null
      }[]
      failures: {
        id: number
        question_id: number
        question_text: string
        category: string | null
        prediction_prob: number | null
        actual_outcome: number | null
        brier_score: number | null
        key_insights: string[]
        created_at: string | null
      }[]
      agent_performance: Record<string, {
        agent_name: string
        samples: number
        avg_brier: number | null
        recent_trend: string | null
      }>
      reflections: unknown[]
      categories: string[]
      contrast_insight: string | null
      has_more: boolean
    }>('/admin/memory', { params: { category: category || undefined, offset } }),
}

// Positions API
export const positionsApi = {
  list: () => apiFetch<import('../types').PositionListResponse>('/positions'),
  
  get: (questionId: number) =>
    apiFetch<{
      question_id: number
      question_title: string
      position: import('../types').Position
      current_price?: number
      unrealized_pnl?: number
      orders: unknown[]
      reviews: import('../types').PositionReview[]
    }>(`/positions/${questionId}`),
  
  runReview: (questionId: number, trigger?: string) =>
    apiFetch<import('../types').PositionReview>(`/positions/${questionId}/review`, {
      method: 'POST',
      params: { trigger: trigger || 'manual' },
    }),
  
  reviewHistory: (questionId: number, limit?: number) =>
    apiFetch<{
      question_id: number
      reviews: import('../types').PositionReview[]
      count: number
    }>(`/positions/${questionId}/reviews`, { params: { limit: limit || 20 } }),
  
  dueReviews: () =>
    apiFetch<{ positions: unknown[]; count: number }>('/positions/due-reviews'),
  
  runAllReviews: () =>
    apiFetch<{ reviews_run: number; action_summary: Record<string, number>; results: unknown[] }>(
      '/positions/run-all-reviews',
      { method: 'POST' }
    ),

  settle: (dryRun: boolean = false) =>
    apiFetch<{
      settled_count: number
      total_realized_pnl: number
      total_cost_basis: number
      total_roi_percent: number
      winners: number
      losers: number
      win_rate: number
      results: Array<{
        question_id: number
        question_title: string
        outcome: string
        yes_qty: number
        avg_cost: number
        settlement_value: number
        cost_basis: number
        realized_pnl: number
        roi_percent: number
      }>
    }>('/positions/settle', {
      method: 'POST',
      params: { dry_run: dryRun },
    }),
}

// Wallet Dashboard API types
export interface WalletOverview {
  by_type: Record<string, { count: number; avg_win_rate: number; total_volume: number }>
  total_wallets: number
  total_volume: number
}

export interface CategoryPerformance {
  category: string
  wallets: number
  markets: number
  volume_usd: number
  win_rate: number
}

export interface SpecializationStats {
  by_type: Record<string, { wallets: number; avg_win_rate: number; avg_markets: number; volume_millions: number }>
  total_wallets: number
  specialist_pct: number
  generalist_pct: number
}

export interface TopWallet {
  wallet: string
  address: string
  win_rate: number
  wins: number
  total: number
  volume_usd: number
}

export interface WalletDashboard {
  overview: WalletOverview
  specialization: SpecializationStats
  category_performance: {
    smart_money: CategoryPerformance[]
    all: CategoryPerformance[]
  }
  top_wallets: TopWallet[]
  updated_at: string
}

export interface WalletDetail {
  address: string
  type: string
  strategy_type: string | null
  best_category: string | null
  worst_category: string | null
  total_volume_usd: number
  trade_count: number
  unique_markets: number
  resolved_trades: number
  winning_trades: number
  win_rate: number
  confidence: number
  expectancy: number | null
  profit_factor: number | null
  avg_entry_odds: number | null
  estimated_pnl: number
  roi_pct: number
  resolved_volume: number
  updated_at: string | null
  categories: {
    category: string
    markets: number
    wins: number
    win_rate: number
    volume_usd: number
  }[]
  category_performance: Record<string, {
    win_rate: number
    trades: number
    pnl: number
  }> | null
}

export interface HotMarket {
  question_id: number
  title: string
  event_title: string
  outcome_name: string | null
  category: string | null
  close_ts: string | null
  sm_yes_volume: number
  sm_no_volume: number
  sm_total_volume: number
  sm_wallet_count: number
  sm_yes_pct: number
  sm_sentiment: 'STRONG YES' | 'YES' | 'NEUTRAL' | 'NO' | 'STRONG NO'
}

export interface WalletListItem {
  address: string
  wallet: string
  wallet_type: string
  strategy_type: string | null
  // Detailed classification (arbitrageur, market_maker, directional, hybrid)
  trader_type: string | null
  trading_style: string | null  // whale, consistent_winner, value_hunter, etc.
  trader_tier: string | null  // elite, professional, skilled, average, below_average
  best_category: string | null
  win_rate: number
  wins: number
  losses: number
  total: number
  volume_usd: number
  unique_markets: number
  expectancy: number | null
  profit_factor: number | null
  // Behavior metrics
  hedge_ratio: number | null  // 0-1, % of volume hedged (both sides)
  sell_ratio: number | null   // 0-1, % of volume as sells
  roi: number | null          // P&L / Volume
  avg_trade_size: number
  last_trade_at: string | null
}

export interface WalletListResponse {
  wallets: WalletListItem[]
  total: number
  page: number
  page_size: number
}

// Smart Money Dashboard types
export interface TraderTypeBreakdown {
  trader_type: string
  count: number
  avg_win_rate: number
  avg_roi_pct: number
  avg_expectancy: number
  avg_profit_factor: number
  total_volume: number
  avg_hedge_ratio_pct: number
  avg_sell_ratio_pct: number
}

export interface TradingStyleBreakdown {
  trading_style: string
  count: number
  avg_win_rate: number
  avg_roi_pct: number
  avg_expectancy: number
  avg_profit_factor: number
  total_volume: number
  avg_trades: number
}

export interface TierBreakdown {
  trader_tier: string
  count: number
  pct_of_total: number
  avg_win_rate: number
  avg_roi_pct: number
  avg_expectancy: number
  avg_profit_factor: number
  total_volume: number
  roi_range: { min: number; max: number }
}

export interface EnhancedCategoryPerformance {
  category: string
  wallets: number
  markets: number
  volume_usd: number
  avg_win_rate: number
  avg_expectancy: number
  avg_profit_factor: number
  avg_roi_pct: number
  total_pnl: number
}

export interface WalletComparisonItem extends WalletListItem {
  worst_category: string | null
  total_pnl: number | null
  category_performance: Record<string, any>
  percentiles: {
    win_rate_percentile: number | null
    roi_percentile: number | null
    volume_percentile: number | null
    expectancy_percentile: number | null
  }
}

export interface ComparisonInsight {
  type: string
  wallet?: string
  address?: string
  value?: number
  category?: string
  wallets?: string[]
  message: string
}

export interface WalletComparison {
  wallets: WalletComparisonItem[]
  rankings: Record<string, { address: string; value: number; rank: number }[]>
  insights: ComparisonInsight[]
  wallet_count: number
}

export interface EnhancedWalletListResponse {
  wallets: WalletListItem[]
  total: number
  page: number
  page_size: number
  filters_applied: {
    wallet_types: string[] | null
    trader_types: string[] | null
    trading_styles: string[] | null
    trader_tiers: string[] | null
    best_categories: string[] | null
  }
  sort: {
    primary: { field: string; order: string }
    secondary: { field: string; order: string } | null
  }
}

export type WalletSortField = 'win_rate' | 'volume_usd' | 'total' | 'unique_markets' | 'avg_trade_size' | 'expectancy' | 'profit_factor' | 'roi' | 'hedge_ratio' | 'sell_ratio' | 'wins' | 'losses'

// Wallet Dashboard API
export const walletsApi = {
  dashboard: () => apiFetch<WalletDashboard>('/wallets/dashboard'),

  overview: () => apiFetch<WalletOverview>('/wallets/overview'),

  categories: (walletType: string = 'smart_money') =>
    apiFetch<CategoryPerformance[]>('/wallets/categories', { params: { wallet_type: walletType } }),

  specialization: () => apiFetch<SpecializationStats>('/wallets/specialization'),

  top: (params: { wallet_type?: string; limit?: number; min_markets?: number } = {}) =>
    apiFetch<TopWallet[]>('/wallets/top', { params }),

  list: (params: {
    wallet_type?: string
    sort_by?: WalletSortField
    sort_order?: 'asc' | 'desc'
    page?: number
    page_size?: number
    min_markets?: number
  } = {}) => apiFetch<WalletListResponse>('/wallets/list', { params }),

  categorySpecialists: (category: string, limit: number = 10) =>
    apiFetch<TopWallet[]>(`/wallets/categories/${category}/specialists`, { params: { limit } }),

  detail: (address: string) => apiFetch<WalletDetail>(`/wallets/${address}`),

  hotMarkets: (params: { min_volume?: number; lookback_days?: number; limit?: number } = {}) =>
    apiFetch<HotMarket[]>('/wallets/hot-markets', { params }),

  // Breakdown endpoints
  traderTypeBreakdown: () => apiFetch<TraderTypeBreakdown[]>('/wallets/breakdown/trader-types'),

  tradingStyleBreakdown: () => apiFetch<TradingStyleBreakdown[]>('/wallets/breakdown/trading-styles'),

  tierBreakdown: () => apiFetch<TierBreakdown[]>('/wallets/breakdown/tiers'),

  enhancedCategories: (walletType?: string) =>
    apiFetch<EnhancedCategoryPerformance[]>('/wallets/categories/enhanced', {
      params: { wallet_type: walletType },
    }),

  // Comparison endpoint
  compare: (addresses: string[]) =>
    apiFetch<WalletComparison>('/wallets/compare', {
      method: 'POST',
      body: JSON.stringify({ addresses }),
    }),

  // Enhanced list with advanced filtering
  listEnhanced: (params: {
    wallet_types?: string[]
    trader_types?: string[]
    trading_styles?: string[]
    trader_tiers?: string[]
    best_categories?: string[]
    sort_by?: WalletSortField
    sort_order?: 'asc' | 'desc'
    secondary_sort_by?: WalletSortField
    secondary_sort_order?: 'asc' | 'desc'
    page?: number
    page_size?: number
    min_markets?: number
  } = {}) => {
    // Convert arrays to comma-separated strings for API
    const queryParams: Record<string, any> = { ...params }
    if (params.wallet_types) queryParams.wallet_types = params.wallet_types.join(',')
    if (params.trader_types) queryParams.trader_types = params.trader_types.join(',')
    if (params.trading_styles) queryParams.trading_styles = params.trading_styles.join(',')
    if (params.trader_tiers) queryParams.trader_tiers = params.trader_tiers.join(',')
    if (params.best_categories) queryParams.best_categories = params.best_categories.join(',')
    return apiFetch<EnhancedWalletListResponse>('/wallets/list', { params: queryParams })
  },
}

// Forecast API types
export interface ForecastAnalysis {
  raw_question: string
  normalized_question: string
  question_type: string
  category: string
  primary_entity: string | null
  secondary_entity: string | null
  threshold_value: number | null
  threshold_unit: string | null
  target_date: string | null
  resolution_criteria: string
  ambiguities: string[]
  suggested_topics: string[]
  analysis_confidence: number
}

export interface ForecastReport {
  question: string
  probability: number
  confidence: number
  summary: string
  key_factors: string[]
  supporting_evidence: string[]
  risk_factors: string[]
  category: string
  timestamp: string
}

export interface ForecastResult {
  compiled: {
    title: string
    description: string
    category: string
    close_ts: string | null
    metadata: Record<string, unknown>
    topics: string[]
    analysis: ForecastAnalysis
  }
  prediction: Record<string, unknown>
  report: ForecastReport
  report_markdown: string
}

export interface ForecastStatus {
  forecast_id: string
  question: string
  status: 'pending' | 'analyzing' | 'running' | 'completed' | 'failed'
  stage: string | null
  progress: number
  analysis: ForecastAnalysis | null
  result: ForecastResult | null
  error: string | null
  started_at: string | null
  completed_at: string | null
}

export interface ForecastHistoryItem {
  forecast_id: string
  question: string
  status: string
  probability: number | null
  confidence: number | null
  category: string | null
  started_at: string | null
  completed_at: string | null
}

// Forecast API
export const forecastApi = {
  analyze: (question: string) =>
    apiFetch<ForecastAnalysis>('/forecast/analyze', {
      method: 'POST',
      body: JSON.stringify({ question }),
    }),

  analyzeStream: (
    question: string,
    onProgress: (stage: string, data: any) => void,
    onComplete: (analysis: ForecastAnalysis) => void,
    onError: (error: string) => void
  ): (() => void) => {
    const url = `${API_BASE}/forecast/analyze/stream`
    const apiKey = getStoredApiKey()

    // Use fetch with POST for SSE (EventSource doesn't support POST)
    const controller = new AbortController()

    fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(apiKey ? { 'X-API-Key': apiKey } : {}),
      },
      body: JSON.stringify({ question }),
      signal: controller.signal,
    })
      .then(async (response) => {
        const reader = response.body?.getReader()
        if (!reader) throw new Error('No reader')

        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          // Parse SSE events from buffer
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          let eventType = ''
          let eventData = ''

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7)
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6)
            } else if (line === '' && eventType && eventData) {
              try {
                const parsed = JSON.parse(eventData)
                if (eventType === 'progress') {
                  onProgress(parsed.stage, parsed.data)
                } else if (eventType === 'complete') {
                  onComplete(parsed.analysis)
                } else if (eventType === 'error') {
                  onError(parsed.message)
                }
              } catch (e) {
                console.error('Failed to parse SSE event:', e)
              }
              eventType = ''
              eventData = ''
            }
          }
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          onError(err.message || 'Connection error')
        }
      })

    return () => controller.abort()
  },

  start: (question: string, overrides?: { category?: string; target_date?: string; topics?: string[]; primary_entity?: string; clarification?: string }) =>
    apiFetch<{ forecast_id: string; question: string; status: string; message: string }>(
      '/forecast/start',
      {
        method: 'POST',
        body: JSON.stringify({ question, ...overrides }),
      }
    ),

  status: (forecastId: string) =>
    apiFetch<ForecastStatus>(`/forecast/status/${forecastId}`),

  detail: (forecastId: string) =>
    apiFetch<ForecastStatus>(`/forecast/${forecastId}`),

  history: (limit?: number, status?: string) =>
    apiFetch<{ forecasts: ForecastHistoryItem[]; total: number }>('/forecast/history', {
      params: { limit: limit || 20, status: status || undefined },
    }),

  delete: (forecastId: string) =>
    apiFetch<{ message: string }>(`/forecast/${forecastId}`, { method: 'DELETE' }),
}

// SSE streaming for forecasts
export function streamForecast(
  forecastId: string,
  onProgress: (data: { status: string; stage: string | null; progress: number }) => void,
  onComplete: (result: ForecastResult) => void,
  onError: (error: string) => void
): () => void {
  const apiKey = getStoredApiKey()
  const params = new URLSearchParams()
  if (apiKey) params.set('api_key', apiKey)
  const url = `${API_BASE}/forecast/stream/${forecastId}${params.toString() ? `?${params}` : ''}`
  const eventSource = new EventSource(url, { withCredentials: true })

  eventSource.addEventListener('progress', (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data)
      onProgress(data)
    } catch (err) {
      console.error('Failed to parse progress event:', err)
    }
  })

  eventSource.addEventListener('complete', (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data)
      onComplete(data.result)
      eventSource.close()
    } catch (err) {
      console.error('Failed to parse complete event:', err)
    }
  })

  eventSource.addEventListener('error', (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data)
      onError(data.error || 'Unknown error')
    } catch {
      onError('Connection error')
    }
    eventSource.close()
  })

  eventSource.onerror = () => {
    onError('Connection to server lost')
    eventSource.close()
  }

  return () => eventSource.close()
}

// Calibration API types
export interface ZoneAccuracy {
  accuracy: number
  total: number
}

export interface CalibrationMetrics {
  total_predictions: number
  resolved_predictions: number
  avg_brier: number | null
  avg_log_loss: number | null
  weighted_brier: number | null
  total_weight: number
  accuracy_by_zone: Record<string, ZoneAccuracy>
  algorithm_version: string | null
  oldest_prediction: string | null
  newest_prediction: string | null
  bullish_accuracy: number | null
  bearish_accuracy: number | null
  avg_edge: number | null
  rolling_window_days: number
}

export interface VersionComparisonItem {
  version: string
  resolved_predictions: number
  avg_brier: number | null
  weighted_brier: number | null
  avg_log_loss: number | null
  oldest_prediction: string | null
  newest_prediction: string | null
}

export interface VersionComparisonResponse {
  rolling_window_days: number
  versions: VersionComparisonItem[]
  current_version: string
}

export interface CalibrationSettings {
  algorithm_version: string
  rolling_window_days: number
  time_decay_lambda: number
  min_samples_for_metrics: number
}

// Calibration API
export const calibrationApi = {
  metrics: (params: { rolling_days?: number; version?: string; category?: string } = {}) =>
    apiFetch<CalibrationMetrics>('/calibration/metrics', { params }),

  byVersion: (rolling_days?: number) =>
    apiFetch<VersionComparisonResponse>('/calibration/by-version', {
      params: { rolling_days },
    }),

  currentVersion: () => apiFetch<CalibrationSettings>('/calibration/current-version'),
}

// SSE streaming for runs
export function streamRun(
  questionId: number,
  onEvent: (event: import('../types').RunStreamEvent) => void,
  onError: (error: Error) => void,
  paperTrade: boolean = false
): () => void {
  const apiKey = getStoredApiKey()
  const params = new URLSearchParams()
  if (paperTrade) params.set('paper_trade', 'true')
  if (apiKey) params.set('api_key', apiKey)
  const url = `${API_BASE}/runs/question/${questionId}/stream${params.toString() ? `?${params}` : ''}`
  const eventSource = new EventSource(url, { withCredentials: true })
  
  const handleEvent = (type: string) => (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data)
      onEvent({ type: type as import('../types').RunStreamEvent['type'], data })
    } catch (err) {
      console.error('Failed to parse SSE event:', err)
    }
  }
  
  eventSource.addEventListener('start', handleEvent('start'))
  eventSource.addEventListener('agent_progress', handleEvent('agent_progress'))
  eventSource.addEventListener('agent_complete', handleEvent('agent_complete'))
  eventSource.addEventListener('search_activity', handleEvent('search_activity'))
  eventSource.addEventListener('complete', handleEvent('complete'))
  eventSource.addEventListener('error', handleEvent('error'))
  
  eventSource.onerror = () => {
    onError(new Error('Connection to server lost'))
    eventSource.close()
  }
  
  return () => eventSource.close()
}
